/**
 * @file 订单详情抽屉组件
 * @description 显示顺风车订单详情信息，包含地图标记上下车位置
 */
<template>
  <el-drawer
    :visible.sync="visible"
    :title="'订单详情'"
    size="600px"
    :before-close="handleClose"
    :append-to-body="true"
    class="order-detail-drawer">
    <div class="drawer-content" v-loading="loading">
      <!-- 订单基本信息 -->
      <div class="info-section">
        <div class="section-title">
          <i class="el-icon-tickets"></i>
          <span>订单信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item full">
            <span class="label">订单编号</span>
            <span class="value">{{ orderData.orderNo || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">订单状态</span>
            <span class="value">
              <el-tag type="primary" size="small" v-if="orderData.status == 10">新订单</el-tag>
              <el-tag type="success" size="small" v-else-if="orderData.status > 10 && orderData.status < 100">已接单</el-tag>
              <el-tag type="info" size="small" v-else>已取消</el-tag>
            </span>
          </div>
          <div class="info-item">
            <span class="label">订单类型</span>
            <span class="value">
              <el-tag :type="orderData.poolType == 1 ? 'primary' : 'success'" size="small">
                {{ orderData.poolType == 1 ? '独享' : '拼车' }}
              </el-tag>
            </span>
          </div>
          <div class="info-item">
            <span class="label">下单时间</span>
            <span class="value">{{ orderData.created | filterTime }}</span>
          </div>
          <div class="info-item" v-if="orderData.partnerOrderId">
            <span class="label">三方订单号</span>
            <span class="value">{{ orderData.partnerOrderId }}</span>
          </div>
          <div class="info-item" v-if="orderData.channelAlias">
            <span class="label">订单来源</span>
            <span class="value">{{ orderData.channelAlias | filterConst('HITCHHIKINGCHANNEL') }}</span>
          </div>
        </div>
      </div>

      <!-- 线路信息 -->
      <div class="info-section">
        <div class="section-title">
          <i class="el-icon-position"></i>
          <span>线路信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item full">
            <span class="label">城市/线路</span>
            <span class="value highlight">
              {{ orderData.startCity }} - {{ orderData.endCity }}
              <el-tag :type="orderData.cityType == 1 ? 'warning' : 'success'" size="small" style="margin-left: 8px;">
                {{ orderData.cityType == 1 ? '城际' : '市内' }}线路
              </el-tag>
            </span>
          </div>
          <div class="info-item">
            <span class="label">计价里程</span>
            <span class="value">{{ orderData.distance || 0 }} km</span>
          </div>
          <div class="info-item">
            <span class="label">单价</span>
            <span class="value highlight">{{ ((orderData.amount / orderData.distance) || 0).toFixed(2) }} 元/公里</span>
          </div>
        </div>
      </div>

      <!-- 出发时间 -->
      <div class="info-section">
        <div class="section-title">
          <i class="el-icon-time"></i>
          <span>出发时间</span>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">最早出发</span>
            <span class="value">{{ orderData.earliestDepartureTime | filterTime }}</span>
          </div>
          <div class="info-item">
            <span class="label">最晚出发</span>
            <span class="value">{{ orderData.latestDepartureTime | filterTime }}</span>
          </div>
        </div>
      </div>

      <!-- 乘客信息 -->
      <div class="info-section">
        <div class="section-title">
          <i class="el-icon-user"></i>
          <span>乘客信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">乘客</span>
            <span class="value">用户{{ orderData.realPhoneSuffix || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">乘车人数</span>
            <span class="value">{{ orderData.passengerNum || 1 }}人</span>
          </div>
        </div>
      </div>

      <!-- 上下车位置 -->
      <div class="info-section">
        <div class="section-title">
          <i class="el-icon-location"></i>
          <span>上下车位置</span>
        </div>
        <div class="location-info">
          <div class="location-item start">
            <div class="location-icon">
              <i class="el-icon-location"></i>
            </div>
            <div class="location-content">
              <div class="location-label">上车地点</div>
              <div class="location-address">{{ orderData.startPointName || '-' }}</div>
              <div class="location-detail" v-if="orderData.startPointAddress">{{ orderData.startPointAddress }}</div>
            </div>
          </div>
          <div class="location-line"></div>
          <div class="location-item end">
            <div class="location-icon">
              <i class="el-icon-location-outline"></i>
            </div>
            <div class="location-content">
              <div class="location-label">下车地点</div>
              <div class="location-address">{{ orderData.endPointName || '-' }}</div>
              <div class="location-detail" v-if="orderData.endPointAddress">{{ orderData.endPointAddress }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 地图展示 -->
      <div class="info-section map-section">
        <div class="section-title">
          <i class="el-icon-map-location"></i>
          <span>位置地图</span>
        </div>
        <div class="map-container" ref="mapContainer"></div>
      </div>

      <!-- 费用信息 -->
      <div class="info-section">
        <div class="section-title">
          <i class="el-icon-money"></i>
          <span>费用信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">订单金额</span>
            <span class="value price highlight">￥{{ orderData.amount || orderData.commissionAmount || 0 }}</span>
          </div>
          <div class="info-item" v-if="orderData.cancelFee">
            <span class="label">取消费用</span>
            <span class="value price">
              ￥{{ orderData.cancelFee }}
              <span v-if="orderData.cancelRatio" style="color: #909399;">({{ orderData.cancelRatio }}取消费率)</span>
            </span>
          </div>
        </div>
      </div>

      <!-- 取消原因 -->
      <div class="info-section" v-if="orderData.reason">
        <div class="section-title">
          <i class="el-icon-warning-outline"></i>
          <span>取消原因</span>
        </div>
        <div class="info-grid">
          <div class="info-item full">
            <span class="label">原因</span>
            <span class="value">{{ orderData.reason }}</span>
          </div>
        </div>
      </div>

      <!-- 车辆司机信息 -->
      <div class="info-section" v-if="orderData.licenseNo || orderData.driverName">
        <div class="section-title">
          <i class="el-icon-truck"></i>
          <span>接单信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">车辆号牌</span>
            <span class="value">{{ orderData.licenseNo || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">司机姓名</span>
            <span class="value">{{ orderData.driverName || '-' }}</span>
          </div>
          <div class="info-item" v-if="orderData.driverPhone">
            <span class="label">司机电话</span>
            <span class="value">{{ orderData.driverPhone }}</span>
          </div>
          <div class="info-item" v-if="orderData.vehicleSeat">
            <span class="label">座位数</span>
            <span class="value">{{ orderData.vehicleSeat }}座</span>
          </div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import AMap from "AMap"

export default {
  name: 'OrderDetailDrawer',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      orderData: {},
      map: null,
      markers: []
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        this.visible = val
        if (val && this.order) {
          this.orderData = { ...this.order }
          this.$nextTick(() => {
            this.initMap()
          })
        }
      }
    },
    visible(val) {
      if (!val) {
        this.$emit('update:show', false)
        this.destroyMap()
      }
    }
  },
  methods: {
    handleClose() {
      this.visible = false
    },
    
    getStatusType(status) {
      const statusMap = {
        5: 'warning',   // 待指派
        10: 'primary',  // 已指派
        20: 'success',  // 已完成
        60: 'info',     // 已退票
        70: 'danger'    // 已取消
      }
      return statusMap[status] || 'info'
    },
    
    initMap() {
      if (!this.$refs.mapContainer) return
      
      // 销毁旧地图
      this.destroyMap()
      
      // 创建地图实例
      this.map = new AMap.Map(this.$refs.mapContainer, {
        zoom: 12,
        resizeEnable: true
      })
      
      // 添加上下车位置标记
      this.addLocationMarkers()
    },
    
    addLocationMarkers() {
      if (!this.map) return
      
      const order = this.orderData
      const markersToFit = []
      
      // 上车位置 - 使用顺风车订单字段 startPointLng/startPointLat
      const startLng = order.startPointLng
      const startLat = order.startPointLat
      const startName = order.startPointName || '上车点'
      
      if (startLng && startLat) {
        // 创建上车点标记
        const startMarker = new AMap.Marker({
          position: new AMap.LngLat(startLng, startLat),
          icon: new AMap.Icon({
            size: new AMap.Size(25, 34),
            image: 'https://webapi.amap.com/theme/v1.3/markers/n/start.png',
            imageSize: new AMap.Size(25, 34)
          }),
          anchor: 'bottom-center'
        })
        
        // 添加标签
        startMarker.setLabel({
          offset: new AMap.Pixel(0, -40),
          content: `<div style="
            padding: 8px 12px;
            background: #67c23a;
            color: #fff;
            border-radius: 4px;
            font-size: 13px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.2);
            white-space: nowrap;
          ">上车：${startName}</div>`,
          direction: 'top'
        })
        
        this.map.add(startMarker)
        this.markers.push(startMarker)
        markersToFit.push(startMarker)
      }
      
      // 下车位置 - 使用顺风车订单字段 endPointLng/endPointLat
      const endLng = order.endPointLng
      const endLat = order.endPointLat
      const endName = order.endPointName || '下车点'
      
      if (endLng && endLat) {
        // 创建下车点标记
        const endMarker = new AMap.Marker({
          position: new AMap.LngLat(endLng, endLat),
          icon: new AMap.Icon({
            size: new AMap.Size(25, 34),
            image: 'https://webapi.amap.com/theme/v1.3/markers/n/end.png',
            imageSize: new AMap.Size(25, 34)
          }),
          anchor: 'bottom-center'
        })
        
        // 添加标签
        endMarker.setLabel({
          offset: new AMap.Pixel(0, -40),
          content: `<div style="
            padding: 8px 12px;
            background: #f56c6c;
            color: #fff;
            border-radius: 4px;
            font-size: 13px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.2);
            white-space: nowrap;
          ">下车：${endName}</div>`,
          direction: 'top'
        })
        
        this.map.add(endMarker)
        this.markers.push(endMarker)
        markersToFit.push(endMarker)
      }
      
      // 如果有两个点，绘制连线
      if (startLng && startLat && endLng && endLat) {
        const polyline = new AMap.Polyline({
          path: [
            new AMap.LngLat(startLng, startLat),
            new AMap.LngLat(endLng, endLat)
          ],
          strokeColor: '#409EFF',
          strokeWeight: 4,
          strokeStyle: 'dashed',
          strokeOpacity: 0.8
        })
        this.map.add(polyline)
        this.markers.push(polyline)
      }
      
      // 自适应视野
      if (markersToFit.length > 0) {
        this.map.setFitView(markersToFit, false, [60, 60, 60, 60])
      } else {
        // 没有坐标时，尝试使用地址进行地理编码
        this.geocodeAddress()
      }
    },
    
    geocodeAddress() {
      const order = this.orderData
      const startAddress = order.startPointAddress || order.startPointName
      const endAddress = order.endPointAddress || order.endPointName
      
      if (!startAddress && !endAddress) return
      
      AMap.plugin('AMap.Geocoder', () => {
        const geocoder = new AMap.Geocoder()
        const markersToFit = []
        
        const addMarkerByAddress = (address, name, isStart) => {
          return new Promise((resolve) => {
            geocoder.getLocation(address, (status, result) => {
              if (status === 'complete' && result.geocodes.length) {
                const location = result.geocodes[0].location
                const marker = new AMap.Marker({
                  position: location,
                  icon: new AMap.Icon({
                    size: new AMap.Size(25, 34),
                    image: isStart 
                      ? 'https://webapi.amap.com/theme/v1.3/markers/n/start.png'
                      : 'https://webapi.amap.com/theme/v1.3/markers/n/end.png',
                    imageSize: new AMap.Size(25, 34)
                  }),
                  anchor: 'bottom-center'
                })
                
                marker.setLabel({
                  offset: new AMap.Pixel(0, -40),
                  content: `<div style="
                    padding: 8px 12px;
                    background: ${isStart ? '#67c23a' : '#f56c6c'};
                    color: #fff;
                    border-radius: 4px;
                    font-size: 13px;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                    white-space: nowrap;
                  ">${isStart ? '上车' : '下车'}：${name}</div>`,
                  direction: 'top'
                })
                
                this.map.add(marker)
                this.markers.push(marker)
                markersToFit.push(marker)
              }
              resolve()
            })
          })
        }
        
        const promises = []
        const startName = order.startPointName || '上车点'
        const endName = order.endPointName || '下车点'
        if (startAddress) promises.push(addMarkerByAddress(startAddress, startName, true))
        if (endAddress) promises.push(addMarkerByAddress(endAddress, endName, false))
        
        Promise.all(promises).then(() => {
          if (markersToFit.length >= 1) {
            this.map.setFitView(markersToFit, false, [60, 60, 60, 60])
          }
        })
      })
    },
    
    destroyMap() {
      if (this.map) {
        this.markers.forEach(marker => {
          this.map.remove(marker)
        })
        this.markers = []
        this.map.destroy()
        this.map = null
      }
    }
  },
  beforeDestroy() {
    this.destroyMap()
  }
}
</script>

<style lang="less" scoped>
.order-detail-drawer {
  /deep/ .el-drawer__header {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px solid #e4e7ed;
    
    span {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }
  
  /deep/ .el-drawer__body {
    padding: 0;
    overflow-y: auto;
  }
}

.drawer-content {
  padding: 20px;
}

.info-section {
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .section-title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
    
    i {
      font-size: 16px;
      color: @color-primary;
      margin-right: 8px;
    }
    
    span {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  .info-item {
    display: flex;
    align-items: flex-start;
    
    &.full {
      grid-column: span 2;
    }
    
    .label {
      flex-shrink: 0;
      width: 80px;
      font-size: 13px;
      color: #909399;
    }
    
    .value {
      flex: 1;
      font-size: 13px;
      color: #303133;
      word-break: break-all;
      
      &.highlight {
        color: @color-primary;
        font-weight: 500;
      }
      
      &.price {
        color: #ff6600;
        font-weight: 600;
      }
      
      &.discount {
        color: #67c23a;
      }
    }
  }
}

.location-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  
  .location-item {
    display: flex;
    align-items: flex-start;
    
    .location-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
      
      i {
        font-size: 16px;
      }
    }
    
    &.start .location-icon {
      background: rgba(103, 194, 58, 0.1);
      
      i {
        color: #67c23a;
      }
    }
    
    &.end .location-icon {
      background: rgba(245, 108, 108, 0.1);
      
      i {
        color: #f56c6c;
      }
    }
    
    .location-content {
      flex: 1;
      
      .location-label {
        font-size: 12px;
        color: #909399;
        margin-bottom: 4px;
      }
      
      .location-address {
        font-size: 14px;
        color: #303133;
        line-height: 1.4;
      }
      
      .location-detail {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
        line-height: 1.4;
      }
    }
  }
  
  .location-line {
    width: 2px;
    height: 20px;
    background: linear-gradient(to bottom, #67c23a, #f56c6c);
    margin: 8px 0 8px 15px;
  }
}

.map-section {
  .map-container {
    width: 100%;
    height: 280px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e4e7ed;
  }
}
</style>

<style lang="less">
// 地图标记样式（全局）
.map-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .marker-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    
    i {
      font-size: 16px;
      color: #fff;
    }
  }
  
  .marker-label {
    margin-top: 4px;
    padding: 2px 8px;
    background: #fff;
    border-radius: 4px;
    font-size: 12px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
  }
  
  &.start-marker {
    .marker-icon {
      background: #67c23a;
    }
    
    .marker-label {
      color: #67c23a;
    }
  }
  
  &.end-marker {
    .marker-icon {
      background: #f56c6c;
    }
    
    .marker-label {
      color: #f56c6c;
    }
  }
}
</style>
